// components/EmailListUploadModal.jsx — Anamoria SPA
// v0.2 — NEW-171 Phase 3 (PR-2): accept groupParam; thread ?group on POST /leader/allowlist/bulk so a
//   multi-cohort leader adds to the SELECTED cohort's allowlist. (Nov 1, 2026)
// v0.1 — GS-11: leader bulk allowlist upload (paste or CSV -> review -> confirm).
// Posts to POST /leader/allowlist/bulk (anamoria-leader v1.7). Copy per
// pilot-group-spaces-email-upload-copy-v0.1.md (brand-voice reviewed).
import { useState, useMemo } from 'react';
import s from './EmailListUploadModal.module.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parseLine(line) {
  const t = line.trim();
  if (!t) return null;
  let name = '', email = '';
  const angle = t.match(/^(.*)<([^>]+)>\s*$/);          // "Jane Doe <jane@x.com>"
  if (angle)                 { name = angle[1]; email = angle[2]; }
  else if (t.includes('\t')) { const i = t.indexOf('\t');    name = t.slice(0, i); email = t.slice(i + 1); }
  else if (t.includes(','))  { const i = t.lastIndexOf(','); name = t.slice(0, i); email = t.slice(i + 1); }
  else                       { email = t; }
  return { name: name.trim(), email: email.trim().toLowerCase() };
}

function parseText(text) {
  const lines = text.split(/\r?\n/);
  const out = [];
  lines.forEach((line, idx) => {
    const p = parseLine(line);
    if (!p) return;
    // tolerate a header row like "name,email"
    if (idx === 0 && /^name$/i.test(p.name) && /email/i.test(p.email)) return;
    out.push(p);
  });
  return out;
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

export default function EmailListUploadModal({ getApi, groupParam = '', onClose, onAdded }) {
  const [text, setText] = useState('');
  const [rows, setRows] = useState(null);   // null = input stage; array = review stage
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  function ingest(raw) {
    setRows(parseText(raw).map((p, i) => ({ key: `r${i}`, name: p.name, email: p.email })));
  }
  function onFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => ingest(String(reader.result || ''));
    reader.readAsText(file);
  }
  const setRow = (key, patch) => setRows(rs => rs.map(r => (r.key === key ? { ...r, ...patch } : r)));
  const removeRow = (key) => setRows(rs => rs.filter(r => r.key !== key));

  const valid = useMemo(() => (rows || []).map(r => ({
    ...r,
    okEmail: EMAIL_RE.test(r.email),
    okName: r.name.trim().length > 0,
  })), [rows]);
  const canSubmit = valid.length > 0 && valid.every(r => r.okEmail && r.okName) && !submitting;

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true); setError('');
    try {
      const api = getApi();
      const res = await api.post(`/leader/allowlist/bulk${groupParam}`, {
        entries: valid.map(r => ({ name: r.name.trim(), email: r.email.trim() })),
      });
      setResults(res);
      if (res.added > 0) onAdded?.(res);
    } catch {
      setError("We couldn't save these just now. Please try again when you're ready.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={s.overlay} onClick={onClose}>
      <div className={s.box} onClick={e => e.stopPropagation()}>
        <div className={s.head}>
          <h3 className={s.title}>Add the people in your group</h3>
          <button className={s.close} onClick={onClose} aria-label="Close">✕</button>
        </div>

        {results ? (
          /* Results stage */
          <div className={s.body}>
            {results.added > 0 && (
              <p className={s.good}>Added {plural(results.added, 'person', 'people')} to your group.</p>
            )}
            {results.skipped_duplicate > 0 && (
              <p className={s.note}>
                {results.skipped_duplicate} {results.skipped_duplicate === 1 ? 'was' : 'were'} already on your list, so we didn't add {results.skipped_duplicate === 1 ? 'that one' : 'them'} again.
              </p>
            )}
            {results.invalid > 0 && (
              <p className={s.warn}>
                We couldn't read {results.invalid} of {results.invalid === 1 ? 'them' : 'these'} as an email address. You can add {results.invalid === 1 ? 'it' : 'them'} again whenever you're ready.
              </p>
            )}
            <button className={s.primary} onClick={onClose}>Done</button>
          </div>
        ) : rows === null ? (
          /* Input stage */
          <div className={s.body}>
            <p className={s.hint}>Paste your list below, one person per line, with their name and their email. You can also upload a file.</p>
            <p className={s.subhint}>Adding people here just builds your list. No one is emailed until you send invitations.</p>
            <textarea
              className={s.textarea}
              rows={8}
              placeholder={'Jane Doe, jane@example.com\nSam Lee, sam@example.com'}
              value={text}
              onChange={e => setText(e.target.value)}
            />
            <div className={s.row2}>
              <label className={s.fileBtn}>
                Upload a file
                <input type="file" accept=".csv,text/csv,text/plain" onChange={onFile} hidden />
              </label>
              <button className={s.primary} disabled={!text.trim()} onClick={() => ingest(text)}>
                Review the list
              </button>
            </div>
          </div>
        ) : (
          /* Review stage */
          <div className={s.body}>
            <p className={s.hint}>Take a look before you add them. You can fix a name or email, or remove anyone.</p>
            <div className={s.list}>
              {valid.map(r => (
                <div key={r.key} className={s.rev}>
                  <input
                    className={`${s.name} ${r.okName ? '' : s.bad}`}
                    placeholder="Their name" value={r.name} maxLength={100}
                    onChange={e => setRow(r.key, { name: e.target.value })}
                  />
                  <input
                    className={`${s.email} ${r.okEmail ? '' : s.bad}`}
                    placeholder="email@example.com" value={r.email}
                    onChange={e => setRow(r.key, { email: e.target.value.toLowerCase() })}
                  />
                  {!r.okEmail && <span className={s.flag}>Doesn't look like an email yet</span>}
                  {r.okEmail && !r.okName && <span className={s.flag}>Add a name</span>}
                  <button className={s.remove} onClick={() => removeRow(r.key)}>Remove</button>
                </div>
              ))}
            </div>
            {error && <div className={s.errorBanner}>{error}</div>}
            <div className={s.actions}>
              <button className={s.cancel} onClick={() => setRows(null)}>Back</button>
              <button className={s.primary} disabled={!canSubmit} onClick={submit}>
                {submitting ? 'Adding…' : `Add ${plural(valid.length, 'person', 'people')}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
